import { useCallback, useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { Change, ChangeList, ChangeStatus, ChangeType, MergeState, RiskLevel } from '../lib/types';
import { CHANGE_STATUS_LABELS } from '../lib/types';
import { useSession } from '../lib/session';
import { useRememberedCompany } from '../lib/rememberedCompany';
import { formatDate, formatDateTime } from '../lib/dates';

/**
 * Changes: the authority to alter a running system.
 *
 * The merge already works from the task. What this page carries is the part GitLab does not —
 * the risk, the way back, the evidence, and a named person who authorised it. The rule the
 * whole page is built around is that nobody approves their own change, so a change you raised
 * shows you why the button is missing rather than offering it and refusing.
 */
export function ChangesPage() {
  const { session } = useSession();
  const params = useParams();
  const navigate = useNavigate();

  const companies = (session?.companies ?? []).filter((company) => company.capabilities['change.merge'] !== 'none');

  /* Resolved against the companies THIS page offers — only those they may merge in. */
  const { companyId: remembered, remember } = useRememberedCompany(companies);
  const [companyId, setCompanyId] = useState(remembered);
  const [projects, setProjects] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const projectId = params.projectId ?? projects[0]?.id ?? '';

  const [data, setData] = useState<ChangeList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('open_only');
  const [open, setOpen] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    /**
     * A bare array, not `{ projects: [...] }` — which is what this asked for at first, so the
     * picker was permanently empty and the page sat on "Choose a project" with nothing to
     * choose. `?? []` on a missing key fails silently, which is why it looked like no data
     * rather than a wrong shape.
     */
    void api
      .get<Array<{ id: string; name: string; code: string }>>(`/companies/${companyId}/projects`)
      .then((rows) => setProjects(rows ?? []))
      .catch(() => setProjects([]));
  }, [companyId]);

  useEffect(() => {
    if (!params.projectId && projectId) navigate(`/projects/${projectId}/changes`, { replace: true });
  }, [params.projectId, projectId, navigate]);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setData(await api.get<ChangeList>(`/projects/${projectId}/changes?status=${status}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the changes');
    }
  }, [projectId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(what: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await what();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  if (companies.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>Changes are ours to authorise</h3>
          <p>A change alters a running system, and a customer has no part in approving one.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Delivery</div>
          <h1>Changes &amp; merges</h1>
          <p className="subtitle">
            What is going into a running system, who authorised it, and how it gets undone. A
            change is very often a merge request — GitLab carries the diff, this carries the
            authority.
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ marginBottom: 0, minWidth: 140 }}>
            <span className="field-label">Showing</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="open_only">Still live</option>
              <option value="all">Everything</option>
              <option value="submitted">Awaiting approval</option>
              <option value="implemented">Gone in</option>
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0, minWidth: 160 }}>
            <span className="field-label">Company</span>
            <select
              value={companyId}
              onChange={(event) => {
                setCompanyId(event.target.value);
                remember(event.target.value);
                setData(null);
              }}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0, minWidth: 160 }}>
            <span className="field-label">Project</span>
            <select value={projectId} onChange={(event) => navigate(`/projects/${event.target.value}/changes`)}>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      {data && data.awaitingApproval > 0 && (
        <div className="callout mb-2">
          <strong>
            {data.awaitingApproval} change{data.awaitingApproval === 1 ? '' : 's'} waiting for approval.
          </strong>{' '}
          A change you raised yourself is not yours to approve — that is what the approval is for.
        </div>
      )}

      {raising ? (
        <RaiseChange
          projectId={projectId}
          onClose={() => setRaising(false)}
          onDone={async () => {
            setRaising(false);
            await load();
          }}
        />
      ) : (
        <div className="row mb-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!projectId}
            onClick={() => setRaising(true)}
          >
            Raise a change
          </button>
        </div>
      )}

      {!data ? (
        <div className="empty">{projectId ? 'Loading…' : 'Choose a project.'}</div>
      ) : data.changes.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>No changes recorded</h3>
            <p>Nothing matches that filter for {data.project.name}.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>What is changing</th>
                  <th>Kind</th>
                  <th>State</th>
                  <th>Risk</th>
                  <th>Raised by</th>
                  <th>Authorised by</th>
                  <th>Merge</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.changes.map((change) => (
                  <ChangeRow
                    key={change.id}
                    change={change}
                    expanded={open === change.id}
                    busy={busy}
                    onToggle={() => setOpen(open === change.id ? null : change.id)}
                    onAct={act}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ChangeRow({
  change,
  expanded,
  busy,
  onToggle,
  onAct,
}: {
  change: Change;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  onAct: (what: () => Promise<unknown>) => Promise<void>;
}) {
  const [detail, setDetail] = useState<Change | null>(null);
  const ask = useAsk();
  const [rollbackPlan, setRollbackPlan] = useState('');
  const [testEvidence, setTestEvidence] = useState('');
  const [reason, setReason] = useState('');
  /** Asked for on opening a row, not fetched with the list — one call per change is a lot. */
  const [merge, setMerge] = useState<MergeState | null>(null);

  useEffect(() => {
    if (!expanded) return;
    void api
      .get<Change>(`/changes/${change.id}`)
      .then((full) => {
        setDetail(full);
        setRollbackPlan(full.rollbackPlan ?? '');
        setTestEvidence(full.testEvidence ?? '');
      })
      .catch(() => setDetail(null));

    if (change.merge) {
      setMerge(null);
      void api.get<MergeState>(`/changes/${change.id}/merge`).then(setMerge).catch(() => setMerge(null));
    }
  }, [expanded, change.id, change.merge]);

  return (
    <>
      <tr>
        <td className="mono" style={{ fontSize: 11 }}>
          <button type="button" className="link-button" onClick={onToggle}>
            {change.reference}
          </button>
        </td>
        <td>
          <span style={{ fontWeight: 600 }}>{change.title}</span>
          {change.workItem && (
            <div className="dim" style={{ fontSize: 11 }}>
              delivers {change.workItem.key}
            </div>
          )}
          {change.problem && (
            <div className="dim" style={{ fontSize: 11 }}>
              fixes {change.problem.reference}
            </div>
          )}
        </td>
        <td style={{ fontSize: 11 }}>
          <span className={change.type === 'emergency' ? 'badge badge-warn' : 'badge'}>{change.type}</span>
        </td>
        <td>
          <span className={`sprint-state is-${CHANGE_TONE[change.status]}`}>
            {CHANGE_STATUS_LABELS[change.status]}
          </span>
        </td>
        <td style={{ fontSize: 11 }}>
          <span className={change.risk === 'high' ? 'badge badge-warn' : 'badge'}>{change.risk}</span>
          {!change.hasRollbackPlan && change.type !== 'standard' && (
            <div className="dim" style={{ fontSize: 10 }} title="Needed before it can be approved">
              no way back yet
            </div>
          )}
        </td>
        <td style={{ fontSize: 11 }}>{change.requestedBy?.name ?? <span className="dim">—</span>}</td>
        <td style={{ fontSize: 11 }}>
          {change.approvedBy ? (
            <>
              {change.approvedBy.name}
              {change.approvedAt && (
                <div className="dim" style={{ fontSize: 10 }}>
                  {formatDate(change.approvedAt)}
                </div>
              )}
            </>
          ) : (
            <span className="dim">not yet</span>
          )}
        </td>
        <td style={{ fontSize: 11 }}>
          {change.merge ? (
            <>
              <span className="badge badge-accent" title={`${change.merge.sourceBranch} → ${change.merge.targetBranch}`}>
                !{change.merge.iid}
              </span>
              {/*
                Only asserted, or actually confirmed. Shown on the row because an unverified
                claim on a high-risk change is the thing somebody would want to spot.
              */}
              {change.status === 'implemented' && (
                <div style={{ marginTop: 3 }}>
                  {change.mergeVerified ? (
                    <span className="badge badge-ok" title="GitLab confirmed the merge when this was marked in">
                      confirmed
                    </span>
                  ) : (
                    <span
                      className="badge badge-warn"
                      title={
                        change.unverifiedReason
                          ? `Recorded without GitLab confirming it: ${change.unverifiedReason}`
                          : "Nobody could check GitLab — this is somebody's word for it"
                      }
                    >
                      unverified
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <span className="dim">—</span>
          )}
        </td>
        <td className="right">
          <div className="row" style={{ gap: 5, justifyContent: 'flex-end' }}>
            {change.canSubmit && (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={busy}
                onClick={() => void onAct(() => api.post(`/changes/${change.id}/submit`, {}))}
              >
                Submit
              </button>
            )}
            {change.canApprove && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => void onAct(() => api.post(`/changes/${change.id}/approve`, {}))}
              >
                Approve
              </button>
            )}
            {/* Said out loud, because a missing button with no reason reads as a bug. */}
            {change.approvalBlockedReason && (
              <span className="dim" style={{ fontSize: 11 }} title="Somebody else has to look at it">
                {change.approvalBlockedReason}
              </span>
            )}
            {change.canImplement && (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={busy}
                onClick={() =>
                  void onAct(async () => {
                    try {
                      await api.post(`/changes/${change.id}/implement`, {});
                    } catch (err) {
                      /**
                       * GitLab could not confirm the merge. Rather than a dead end, ask for
                       * the sentence the API is asking for — and if they decline, nothing
                       * happens, which is the correct outcome of not being able to say why.
                       */
                      if (err instanceof ApiError && /could not confirm/i.test(err.message)) {
                        const why = await ask.prompt({
                          title: 'GitLab could not confirm the merge',
                          body: `${err.message}\n\nYou can still record it as gone in, but the reason is kept against the change and it will read as unverified.`,
                          label: 'Why are you recording it anyway?',
                          placeholder: 'Merged by hand during the GitLab outage',
                          confirmLabel: 'Record it anyway',
                          minLength: 3,
                        });
                        if (!why?.trim()) throw err;
                        await api.post(`/changes/${change.id}/implement`, { unverifiedReason: why.trim() });
                        return;
                      }
                      throw err;
                    }
                  })
                }
              >
                It went in
              </button>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={9} style={{ background: 'var(--surface-sunken)' }}>
            <div className="stack-sm" style={{ padding: '10px 4px' }}>
              {!detail ? (
                <span className="dim">Loading…</span>
              ) : (
                <>
                  {change.rejectionReason && (
                    <div className="callout">
                      <strong>{change.status === 'rolled_back' ? 'Rolled back:' : 'Rejected:'}</strong>{' '}
                      {change.rejectionReason}
                    </div>
                  )}

                  <div className="row wrap" style={{ gap: 8 }}>
                    <label className="field grow" style={{ marginBottom: 0, minWidth: 260 }}>
                      <span className="field-label">How does this get undone?</span>
                      <input value={rollbackPlan} onChange={(event) => setRollbackPlan(event.target.value)} />
                    </label>
                    <label className="field grow" style={{ marginBottom: 0, minWidth: 260 }}>
                      <span className="field-label">What was tested?</span>
                      <input value={testEvidence} onChange={(event) => setTestEvidence(event.target.value)} />
                    </label>
                  </div>

                  {change.canSubmit && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={busy}
                      onClick={() => void onAct(() => api.patch(`/changes/${change.id}`, { rollbackPlan, testEvidence }))}
                    >
                      Save
                    </button>
                  )}

                  {change.canApprove && (
                    <div className="row wrap" style={{ gap: 7 }}>
                      <input
                        value={reason}
                        placeholder="Why it cannot go in as it stands"
                        onChange={(event) => setReason(event.target.value)}
                        style={{ minWidth: 260 }}
                      />
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={busy || reason.trim().length < 3}
                        onClick={() => void onAct(() => api.post(`/changes/${change.id}/reject`, { reason }))}
                      >
                        Reject it
                      </button>
                    </div>
                  )}

                  {change.status === 'implemented' && (
                    <div className="row wrap" style={{ gap: 7 }}>
                      <input
                        value={reason}
                        placeholder="What went wrong"
                        onChange={(event) => setReason(event.target.value)}
                        style={{ minWidth: 260 }}
                      />
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={busy || reason.trim().length < 3}
                        onClick={() => void onAct(() => api.post(`/changes/${change.id}/rollback`, { reason }))}
                      >
                        We rolled it back
                      </button>
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={busy}
                        onClick={() => void onAct(() => api.post(`/changes/${change.id}/close`, {}))}
                      >
                        Close it
                      </button>
                    </div>
                  )}

                  {change.merge && (
                    <div style={{ fontSize: 12 }}>
                      <span className="dim">GitLab, !{change.merge.iid}: </span>
                      {!merge ? (
                        <span className="dim">asking…</span>
                      ) : merge.checked ? (
                        <span className={merge.merged ? 'badge badge-ok' : 'badge badge-warn'}>
                          {merge.state ?? (merge.merged ? 'merged' : 'not merged')}
                        </span>
                      ) : (
                        /*
                         * Said out loud rather than left blank. "Could not be checked" is not
                         * "fine", and a gate that cannot see is worth knowing about.
                         */
                        <span className="dim" title={merge.reason ?? undefined}>
                          could not be checked — {merge.reason}
                        </span>
                      )}
                    </div>
                  )}

                  {change.implementedAt && (
                    <p className="dim" style={{ margin: 0, fontSize: 12 }}>
                      Went in {formatDateTime(change.implementedAt)}
                      {change.approvedBy ? ` · authorised by ${change.approvedBy.name}` : ' · never authorised'}
                      {change.merge ? (change.mergeVerified ? ' · merge confirmed by GitLab' : ' · merge never confirmed') : ''}
                      {change.unverifiedReason ? ` — recorded anyway: ${change.unverifiedReason}` : ''}
                    </p>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RaiseChange({
  projectId,
  onClose,
  onDone,
}: {
  projectId: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<ChangeType>('normal');
  const [risk, setRisk] = useState<RiskLevel>('medium');
  const [rollbackPlan, setRollbackPlan] = useState('');
  const [mergeIid, setMergeIid] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="card mb-2">
      <div className="card-head">
        <h3>Raise a change</h3>
      </div>
      <div className="card-pad stack-sm">
        {error && <div className="callout">{error}</div>}

        <label className="field" style={{ marginBottom: 0 }}>
          <span className="field-label">What is changing?</span>
          <input
            value={title}
            autoFocus
            placeholder="Add an index to the statements table"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <div className="row wrap" style={{ gap: 8 }}>
          <label className="field" style={{ marginBottom: 0, minWidth: 150 }}>
            <span className="field-label">Kind</span>
            <select value={type} onChange={(event) => setType(event.target.value as ChangeType)}>
              <option value="normal">Normal — needs approval</option>
              <option value="standard">Standard — pre-authorised</option>
              <option value="emergency">Emergency — approve after</option>
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0, minWidth: 120 }}>
            <span className="field-label">Risk</span>
            <select value={risk} onChange={(event) => setRisk(event.target.value as RiskLevel)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0, minWidth: 130 }}>
            <span className="field-label">Merge request</span>
            <input value={mergeIid} placeholder="e.g. 42" onChange={(event) => setMergeIid(event.target.value)} />
          </label>
        </div>

        <label className="field" style={{ marginBottom: 0 }}>
          <span className="field-label">
            How does this get undone?{type !== 'standard' ? ' Needed before anybody can approve it.' : ' Optional.'}
          </span>
          <input value={rollbackPlan} onChange={(event) => setRollbackPlan(event.target.value)} />
        </label>

        <div className="row" style={{ gap: 7 }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || title.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const iid = Number(mergeIid);
                await api.post(`/projects/${projectId}/changes`, {
                  title: title.trim(),
                  type,
                  risk,
                  ...(rollbackPlan.trim() ? { rollbackPlan: rollbackPlan.trim() } : {}),
                  ...(Number.isFinite(iid) && iid > 0 ? { gitlabMergeIid: iid } : {}),
                });
                await onDone();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Could not raise it');
              } finally {
                setBusy(false);
              }
            }}
          >
            Raise it
          </button>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const CHANGE_TONE: Record<ChangeStatus, string> = {
  draft: 'planned',
  submitted: 'planned',
  approved: 'active',
  rejected: 'completed',
  implemented: 'completed',
  closed: 'completed',
  rolled_back: 'completed',
};
